<template>
  <div class="cat-empleado">
    <!-- Loading state del control de acceso -->
    <div v-if="accessLoading" class="d-flex justify-content-center align-items-center" style="min-height: 200px;">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Verificando acceso...</span>
      </div>
    </div>
    
    <!-- Contenido principal cuando se tiene acceso -->
    <div v-else-if="canAccess" class="cat-empleado-content">
      <!-- Toolbar Component -->
      <div class="toolbar-container">
        <ToolbarComponent
          :buttonGroups="[
            {
              id: 'main-actions',
              buttons: [
                {
                  id: 'nuevo',
                  text: 'Nuevo',
                  icon: 'bi bi-plus',
                  variant: 'primary',
                  event: 'new'
                },
                {
                  id: 'actualizar',
                  text: 'Actualizar',
                  icon: 'bi bi-arrow-clockwise',
                  variant: 'secondary',
                  event: 'refresh'
                },
                {
                  id: 'descargar',
                  text: 'Descargar',
                  icon: 'bi bi-download',
                  variant: 'success',
                  event: 'download'
                }
              ]
            }
          ]"
          :rightInfo="`Total: ${empleados.length} empleados`"
          @new="addEmpleado"
          @refresh="loadEmpleados"
          @download="downloadBulk"
        />
      </div>
      
      <!-- Contenido de la página -->
      <div class="page-content">
        <div class="alert alert-info" v-if="loading">Cargando empleados...</div>
        <div class="alert alert-danger" v-if="error">{{ error }}</div>
        
        <div v-if="!loading && !error && (!empleados || empleados.length === 0)">
          <div v-if="!hasEmpresa" class="alert alert-warning">
            Debe elegir una empresa antes de poder continuar.
          </div>
          <div v-else class="alert alert-info">
            No hay empleados registrados. Haga clic en 'Nuevo' para agregar uno.
          </div>
        </div>

        <!-- Tabla con MyDataGrid -->
        <div v-if="!loading && !error && empleados && empleados.length > 0">
          <MyDataGrid
            :columns="dataGridColumns"
            :rows="empleados"
            :enable-pagination="true"
            :per-page="10"
            :page-sizes="[5, 10, 25, 50]"
            :initial-sort="{ key: 'Clave', dir: 'asc' }"
            filter-placeholder="Buscar empleados..."
            :striped="true"
            :compact="true"
            :show-actions="true"
          >
            <!-- Slot personalizado para la columna EsActivo -->
            <template #cell-EsActivo="{ value }">
              <span v-if="value" class="badge bg-success">Sí</span>
              <span v-else class="badge bg-secondary">No</span>
            </template>
            
            <!-- Slot de acciones personalizadas -->
            <template #actions="{ row }">
              <button 
                @click="editEmpleado(row)" 
                class="btn btn-sm btn-outline-primary me-1"
                title="Editar empleado"
              >
                <i class="fas fa-edit"></i>
              </button>
              <button 
                @click="confirmDelete(row.Clave)" 
                class="btn btn-sm btn-outline-danger"
                title="Eliminar empleado"
              >
                <i class="fas fa-trash"></i>
              </button>
            </template>
          </MyDataGrid>
        </div>

        <div class="mt-4">
          <router-link to="/" class="btn btn-secondary">
            <i class="bi bi-arrow-left me-1"></i> Volver al Menú
          </router-link>
        </div>
      </div>
    </div>
    
    <!-- Mensaje cuando no se tiene acceso -->
    <div v-else class="alert alert-warning text-center">
      <h4>Acceso Restringido</h4>
      <p>No tiene permisos para acceder a esta página.</p>
      <router-link to="/" class="btn btn-primary">Volver al Inicio</router-link>
    </div>

    <!-- Modal para Nuevo/Editar Empleado -->
    <div 
      class="modal fade" 
      :class="{ 'show d-block': showModal }"
      id="empleadoModal" 
      tabindex="-1" 
      aria-labelledby="empleadoModalLabel" 
      :aria-hidden="!showModal"
      @click.self="closeModal"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="empleadoModalLabel">
              <i class="bi bi-plus-circle me-2" v-if="!isEditing"></i>
              <i class="bi bi-pencil me-2" v-else></i>
              {{ isEditing ? 'Editar Empleado' : 'Nuevo Empleado' }}
            </h5>
            <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <CatEmpleadoForm
              :empleado="formData"
              :isEditing="isEditing"
              :loading="modalLoading"
              @submit="handleSubmit"
              @cancel="closeModal"
            />
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { useAccessControl } from '@/composables/useAccessControl'
import CatEmpleadoService from '../controllers/CatEmpleadoController';
import MyDataGrid from '@/shared/components/MyDataGrid.vue';
import ToolbarComponent from '@/shared/components/ToolbarComponent.vue';
import CatEmpleadoForm from '../components/CatEmpleadoForm.vue';

export default {
  name: 'CatEmpleadoView',
  components: {
    MyDataGrid,
    ToolbarComponent,
    CatEmpleadoForm
  },
  setup() {
    const { 
      requiresAuth, 
      requiresMenuPermission, 
      canAccess, 
      isLoading: accessLoading 
    } = useAccessControl({
      requiresAuth: true,
      requiresMenuPermission: false
    })

    return {
      requiresAuth,
      requiresMenuPermission,
      canAccess,
      accessLoading
    }
  },
  data() {
    return {
      service: new CatEmpleadoService(),
      // Modal state
      showModal: false,
      modalLoading: false,
      isEditing: false,
      formData: {
        Clave: '',
        EmpresaId: 1,
        Nombre: '',
        ClaveErp: '',
        EsActivo: true,
        UsuarioId: '',
        Clave2: ''
      }
    };
  },
  computed: {
    empleados() {
      return this.service.empleados;
    },
    loading() {
      return this.service.loading;
    },
    error() {
      return this.service.error;
    },
    hasEmpresa() {
      const emp = JSON.parse(localStorage.getItem('empresa_seleccionada') || 'null');
      return !!(emp && emp.EmpresaId);
    },
    // Configuración de columnas para MyDataGrid
    dataGridColumns() {
      return [
        { key: 'Clave', label: 'Clave', sortable: true },
        { key: 'EmpresaId', label: 'ID Empresa', sortable: true },
        { key: 'Nombre', label: 'Nombre', sortable: true },
        { key: 'ClaveErp', label: 'Clave ERP', sortable: true },
        { key: 'UsuarioId', label: 'ID Usuario', sortable: true },
        { key: 'Clave2', label: 'Clave 2', sortable: true },
        { 
          key: 'EsActivo', 
          label: 'Activo', 
          sortable: true,
          format: (value) => value ? 'Sí' : 'No'
        }
      ];
    }
  },
  created() {
    this.service.initialize();
  },
  mounted() {
    // Emitir evento al padre para notificar que esta página tiene toolbar
    this.$emit('toolbar-mounted');
  },
  methods: {
    async loadEmpleados() {
      console.log('Cargando empleados...');
      await this.service.listar();
    },
    addEmpleado() {
      this.isEditing = false;
      this.openModal();
    },
    async editEmpleado(empleado) {
      this.isEditing = true;
      try {
        // Cargar datos completos del empleado
        const response = await this.service.lectura(empleado.Clave);
        if (response && response.data) {
          this.formData = { ...response.data };
        } else {
          this.formData = { ...empleado };
        }
        this.openModal();
      } catch (error) {
        console.error('Error al cargar empleado:', error);
        this.$alert.error('No se pudo cargar el empleado para edición.');
      }
    },
    confirmDelete(clave) {
      console.log('Iniciando confirmación de eliminación para:', clave);
      
      // Usar SweetAlert para la confirmación
      this.$alert.confirm(
        `¿Está seguro que desea eliminar el empleado con clave "${clave}"?`,
        'Confirmar eliminación',
        { icon: 'warning' }
      ).then((result) => {
        console.log('Resultado de confirmación:', result);
        
        if (result.isConfirmed) {
          this.deleteEmpleado(clave);
        }
      });
    },
    async deleteEmpleado(clave) {
      console.log('Ejecutando eliminación para:', clave);
      
      try {
        const response = await this.service.borrar({ Clave: clave });
        console.log('Respuesta de eliminación recibida:', response);
        
        // Verificar si la respuesta es exitosa
        if (response && response.success) {
          // Mostrar mensaje de éxito
          this.$alert.success(response.message || 'Empleado eliminado exitosamente');
          // Recargar datos
          this.service.recargar();
        } else {
          // Si no es exitosa, mostrar error
          this.$alert.error(response.message || 'No se pudo eliminar el empleado. Por favor, intente nuevamente.');
        }
      } catch (error) {
        console.error('Error al eliminar empleado:', error);
        this.$alert.error('No se pudo eliminar el empleado. Por favor, intente nuevamente.');
      }
    },
    async downloadBulk() {
      try {
        await this.service.downloadBulk();
        this.$alert.success('Descarga iniciada correctamente');
      } catch (error) {
        this.$alert.error('Error al descargar: ' + error.message);
      }
    },
    // Métodos del modal
    openModal() {
      if (!this.isEditing) {
        this.resetForm();
      }
      this.showModal = true;
      document.body.classList.add('modal-open');
    },
    closeModal() {
      this.showModal = false;
      this.modalLoading = false;
      this.isEditing = false;
      this.resetForm();
      document.body.classList.remove('modal-open');
    },
    resetForm() {
      this.formData = {
        Clave: '',
        EmpresaId: 1,
        Nombre: '',
        ClaveErp: '',
        EsActivo: true,
        UsuarioId: '',
        Clave2: ''
      };
    },
    async handleSubmit(formData) {
      this.modalLoading = true;
      try {
        const response = await this.service.guardar(formData);
        
        // Verificar si la respuesta es exitosa
        if (response && response.success) {
          // Mostrar mensaje de éxito
          this.$alert.success(response.message || 'Empleado guardado exitosamente');
          // Cerrar modal y recargar datos
          this.closeModal();
          this.service.recargar();
        } else {
          // Si no es exitosa, mostrar error
          this.$alert.error(response.message || 'No se pudo guardar el empleado. Por favor, intente nuevamente.');
        }
      } catch (error) {
        console.error('Error al guardar empleado:', error);
        this.$alert.error('No se pudo guardar el empleado. Por favor, intente nuevamente.');
      } finally {
        this.modalLoading = false;
      }
    }
  }
}
</script>

<style scoped>
.cat-empleado {
  min-height: calc(100vh - 200px);
}

.cat-empleado-content {
  padding: 0;
}

.page-content {
  padding: 1rem;
}

/* Modal styles */
.modal.show {
  display: block !important;
}

/* Forzar centrado del modal */
.modal {
  padding: 0 !important;
}

.modal-dialog {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
  max-width: 600px;
  width: 90%;
  z-index: 1055;
  max-height: 90vh;
  overflow-y: auto;
}

/* Centrado vertical y horizontal */
.modal.show .modal-dialog {
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
}

/* Responsivo para móviles */
@media (max-width: 768px) {
  .modal-dialog {
    width: 95% !important;
    max-width: none !important;
    max-height: 95vh;
  }
}

@media (max-width: 576px) {
  .modal-dialog {
    width: 98% !important;
    max-height: 98vh;
  }
}
</style>
